import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

// Types for DICOM state management
export interface DicomImage {
  id: string;
  url: string;
  thumbnail: string;
  series_number: number;
  instance_number: number;
  metadata: Record<string, any>;
}

export interface MedicalMeasurement {
  id: string;
  name: string;
  value: number;
  unit: string;
  loinc_code?: string;
  confidence?: number;
  method?: string;
  timestamp: string;
  coordinates?: { x: number; y: number }[];
}

export interface EquipmentInfo {
  manufacturer?: string;
  model?: string;
  software_version?: string;
  station_name?: string;
  acquisition_date?: string;
  parameters: Record<string, any>;
}

export interface ViewerSettings {
  zoom: number;
  brightness: number;
  contrast: number;
  annotations: boolean;
  measurements: boolean;
  overlay: boolean;
  pan: { x: number; y: number };
  rotation: number;
  invert: boolean;
}

export interface DicomState {
  // Current DICOM data
  currentStudy: string | null;
  currentSeries: string | null;
  currentImages: DicomImage[];
  selectedImage: DicomImage | null;
  
  // Measurements and annotations
  measurements: MedicalMeasurement[];
  annotations: any[];
  
  // Equipment and metadata
  equipmentInfo: EquipmentInfo | null;
  studyMetadata: Record<string, any>;
  
  // Viewer state
  viewerSettings: ViewerSettings;
  
  // Processing state
  isLoading: boolean;
  isProcessing: boolean;
  error: string | null;
  
  // Cache and performance
  imageCache: Record<string, string>;
  loadingProgress: number;
}

const initialViewerSettings: ViewerSettings = {
  zoom: 1.0,
  brightness: 50,
  contrast: 50,
  annotations: true,
  measurements: true,
  overlay: true,
  pan: { x: 0, y: 0 },
  rotation: 0,
  invert: false,
};

const initialState: DicomState = {
  currentStudy: null,
  currentSeries: null,
  currentImages: [],
  selectedImage: null,
  measurements: [],
  annotations: [],
  equipmentInfo: null,
  studyMetadata: {},
  viewerSettings: initialViewerSettings,
  isLoading: false,
  isProcessing: false,
  error: null,
  imageCache: {},
  loadingProgress: 0,
};

// Async thunks for DICOM operations
export const loadDicomStudy = createAsyncThunk(
  'dicom/loadStudy',
  async (studyId: string, { rejectWithValue }) => {
    try {
      // This will integrate with React Query API layer
      const response = await fetch(`/api/v1/dicom/studies/${studyId}`);
      if (!response.ok) throw new Error('Failed to load DICOM study');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const processDicomImages = createAsyncThunk(
  'dicom/processImages',
  async (studyId: string, { rejectWithValue }) => {
    try {
      const response = await fetch(`/api/v1/dicom/studies/${studyId}/process`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to process DICOM images');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const extractMeasurements = createAsyncThunk(
  'dicom/extractMeasurements',
  async (imageId: string, { rejectWithValue }) => {
    try {
      const response = await fetch(`/api/v1/dicom/images/${imageId}/measurements`);
      if (!response.ok) throw new Error('Failed to extract measurements');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

const dicomSlice = createSlice({
  name: 'dicom',
  initialState,
  reducers: {
    // Image selection and navigation
    selectImage: (state, action: PayloadAction<DicomImage>) => {
      state.selectedImage = action.payload;
    },
    
    setCurrentSeries: (state, action: PayloadAction<string>) => {
      state.currentSeries = action.payload;
    },
    
    // Viewer controls (preserving existing JS functionality)
    updateViewerSettings: (state, action: PayloadAction<Partial<ViewerSettings>>) => {
      state.viewerSettings = { ...state.viewerSettings, ...action.payload };
    },
    
    resetViewerSettings: (state) => {
      state.viewerSettings = initialViewerSettings;
    },
    
    // Measurements management (mirroring existing medical validation)
    addMeasurement: (state, action: PayloadAction<Omit<MedicalMeasurement, 'id' | 'timestamp'>>) => {
      const measurement: MedicalMeasurement = {
        ...action.payload,
        id: `measurement_${Date.now()}`,
        timestamp: new Date().toISOString(),
      };
      state.measurements.push(measurement);
    },
    
    updateMeasurement: (state, action: PayloadAction<{ id: string; data: Partial<MedicalMeasurement> }>) => {
      const index = state.measurements.findIndex(m => m.id === action.payload.id);
      if (index !== -1) {
        const current = state.measurements[index];
        const updated = { 
          ...current,
          ...Object.fromEntries(
            Object.entries(action.payload.data).filter(([_, value]) => value !== undefined)
          )
        } as MedicalMeasurement;
        state.measurements[index] = updated;
      }
    },
    
    deleteMeasurement: (state, action: PayloadAction<string>) => {
      state.measurements = state.measurements.filter(m => m.id !== action.payload);
    },
    
    // Equipment synchronization (replicating equipment_manager.js)
    setEquipmentInfo: (state, action: PayloadAction<EquipmentInfo>) => {
      state.equipmentInfo = action.payload;
    },
    
    updateStudyMetadata: (state, action: PayloadAction<Record<string, any>>) => {
      state.studyMetadata = { ...state.studyMetadata, ...action.payload };
    },
    
    // Cache management
    cacheImage: (state, action: PayloadAction<{ id: string; url: string }>) => {
      state.imageCache[action.payload.id] = action.payload.url;
    },
    
    clearCache: (state) => {
      state.imageCache = {};
    },
    
    // Progress tracking
    setLoadingProgress: (state, action: PayloadAction<number>) => {
      state.loadingProgress = action.payload;
    },
    
    // Error handling
    clearError: (state) => {
      state.error = null;
    },
    
    // Reset state
    resetDicomState: (state) => {
      return { ...initialState, viewerSettings: state.viewerSettings };
    },
  },
  extraReducers: (builder) => {
    builder
      // Load DICOM study
      .addCase(loadDicomStudy.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadDicomStudy.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentStudy = action.payload.study_id;
        state.currentImages = action.payload.images;
        state.studyMetadata = action.payload.metadata;
        state.equipmentInfo = action.payload.equipment_info;
      })
      .addCase(loadDicomStudy.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Process DICOM images
      .addCase(processDicomImages.pending, (state) => {
        state.isProcessing = true;
        state.error = null;
      })
      .addCase(processDicomImages.fulfilled, (state, action) => {
        state.isProcessing = false;
        state.currentImages = action.payload.processed_images;
      })
      .addCase(processDicomImages.rejected, (state, action) => {
        state.isProcessing = false;
        state.error = action.payload as string;
      })
      
      // Extract measurements
      .addCase(extractMeasurements.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(extractMeasurements.fulfilled, (state, action) => {
        state.isLoading = false;
        state.measurements = [...state.measurements, ...action.payload.measurements];
      })
      .addCase(extractMeasurements.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  selectImage,
  setCurrentSeries,
  updateViewerSettings,
  resetViewerSettings,
  addMeasurement,
  updateMeasurement,
  deleteMeasurement,
  setEquipmentInfo,
  updateStudyMetadata,
  cacheImage,
  clearCache,
  setLoadingProgress,
  clearError,
  resetDicomState,
} = dicomSlice.actions;

export default dicomSlice.reducer;
